import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    Tooltip,
    alpha,
    LinearProgress,
    Collapse,
    Skeleton
} from '@mui/material';
import { useSelector } from 'react-redux';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarsIcon from '@mui/icons-material/Stars';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { fetchReferralBonus } from '../../services/backendAPI';
import { useSnackbar } from 'notistack';
import DataCard from '../Common/DataCard';

// --- Static Constants ---

// --- Helper Functions ---

// --- Helper Functions ---
const getStatusColorMod = (status, theme) => {
    switch ((status || '').toLowerCase()) {
        case 'completed': return theme.palette.success.main;
        case 'pending': return theme.palette.warning.main;
        case 'failed': return theme.palette.error.main;
        default: return theme.palette.text.secondary;
    }
};

// --- Sub-Components (Memoized for Performance) ---
const MetricsSkeleton = React.memo(({ theme }) => (
    <Grid container spacing={2} sx={{ mb: 1, flex: 'none' }}>
        {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
                <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <CardContent sx={{ p: '24px !important' }}>
                        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 2 }} />
                    </CardContent>
                </Card>
            </Grid>
        ))}
    </Grid>
));

const ReferralSkeleton = React.memo(({ isMobile, theme }) => (
    <Box sx={{ width: '100%' }}>
        {isMobile ? (
            <Grid container spacing={1}>
                {[1, 2, 3].map((i) => (
                    <Grid item xs={12} key={i}>
                        <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 0.5 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <TableCell key={i}><Skeleton variant="text" width="80%" /></TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <TableRow key={i}>
                                {[1, 2, 3, 4, 5, 6].map((j) => (
                                    <TableCell key={j} sx={{ py: 2 }}><Skeleton variant="rectangular" width="100%" height={15} sx={{ borderRadius: 1 }} /></TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </Box>
));

const SummaryCard = React.memo(({ title, value, icon, color, theme }) => (
    <Card sx={{
        height: '100%',
        background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(theme.palette.background.paper, 0.95)})`
            : `linear-gradient(135deg, ${alpha(color, 0.08)}, ${alpha(theme.palette.background.paper, 0.9)})`,
        borderRadius: 2.5,
        border: `1px solid ${alpha(color, 0.2)}`,
        boxShadow: `0 2px 10px ${alpha(color, 0.05)}`,
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' }
    }}>
        <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: alpha(color, 0.1),
                    color: color,
                    display: 'flex',
                    mr: 1.5,
                    '& .MuiSvgIcon-root': { fontSize: 18 }
                }}>
                    {icon}
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {title}
                </Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.text.primary, ml: 0.5 }}>
                {typeof value === 'number' && title.toLowerCase().includes('bonus') ? `₹${value.toFixed(2)}` : value}
            </Typography>
        </CardContent>
    </Card>
));

const ReferralProgressCell = React.memo(({ current, total, percent, isCurrency = false, label, theme }) => (
    <Box sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, px: 0.25 }}>
            {label ? (
                <>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ fontSize: '0.75rem' }}>
                        {isCurrency ? `₹${current}` : current} / {isCurrency ? `₹${total}` : total} ({percent.toFixed(0)}%)
                    </Typography>
                </>
            ) : (
                <>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {isCurrency ? `₹${current}` : current} / {isCurrency ? `₹${total}` : total}
                    </Typography>
                    <Typography variant="caption" fontWeight={800} color={percent >= 100 ? "success.main" : (percent > 0 ? "primary.main" : "text.disabled")} sx={{ fontSize: '0.65rem' }}>
                        {percent.toFixed(0)}%
                    </Typography>
                </>
            )}
        </Box>
        <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    backgroundColor: percent >= 100 ? theme.palette.success.main : theme.palette.primary.main
                }
            }}
        />
    </Box>
));

const ReferralCard = React.memo(({ item, theme }) => {
    return (
        <DataCard
            id={item.referred_client}
            header={{
                label: "USER ID",
                title: item.referred_client
            }}
            middleSection={{
                label: "BONUS",
                value: item.bonus && parseFloat(item.bonus) > 0 ? `₹${item.bonus}` : '₹0.00',
                color: (item.bonus && parseFloat(item.bonus) > 0) ? theme.palette.success.main : theme.palette.text.primary
            }}
            status={{
                label: item.status || 'Active',
                color: getStatusColorMod(item.status, theme)
            }}
            expandable={true}
            renderExpandedContent={(
                <>
                    <Box sx={{ mb: 3 }}>
                        <ReferralProgressCell
                            label="Transaction Count"
                            current={item.transaction_count.count}
                            total={item.transaction_count.minCount}
                            percent={item.transaction_count.percent}
                            theme={theme}
                        />
                        <ReferralProgressCell
                            label="Transaction Amount"
                            current={item.transaction_amount.amount}
                            total={item.transaction_amount.minAmount}
                            percent={item.transaction_amount.percent}
                            isCurrency
                            theme={theme}
                        />
                    </Box>

                    <Box sx={{ my: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}` }} />

                    <Grid container spacing={2}>
                        <Grid item xs={7}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>NARRATION</Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    lineHeight: 1.2,
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {item.narration && item.narration !== '-' ? item.narration : 'Sign Up Bonus'}
                            </Typography>
                        </Grid>

                        <Grid item xs={5} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>SIGN-UP DATE</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.date}</Typography>
                        </Grid>
                    </Grid>
                </>
            )}
        />
    );
});


const DesktopTableView = React.memo(({ data, theme }) => (
    <TableContainer sx={{
        maxHeight: 'calc(100vh - 180px)', 
        overflow: 'auto',
        borderRadius: 'inherit',
        '&::-webkit-scrollbar': { width: '5px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.divider, 0.2), borderRadius: '10px' }
    }}>
        <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>User ID</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>Narration</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem', minWidth: 100 }}>Date</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>Bonus</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>Transaction Count</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 700, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`, zIndex: 100, fontSize: '0.75rem' }}>Transaction Amount</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((row, index) => (
                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 1, fontWeight: 600, fontSize: '0.8rem' }}>{row.referred_client}</TableCell>
                        <TableCell sx={{ py: 1 }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    lineHeight: 1.2,
                                    fontSize: '0.8rem',
                                    minWidth: 150,
                                    maxWidth: 250,
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {row.narration}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                        <TableCell sx={{ py: 1, color: theme.palette.success.main, fontWeight: 700, fontSize: '0.8rem' }}>
                            {row.bonus && parseFloat(row.bonus) > 0 ? `₹${row.bonus}` : '-'}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                            <Box sx={{
                                display: 'inline-block',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: alpha(getStatusColorMod(row.status, theme), 0.1),
                                color: getStatusColorMod(row.status, theme),
                                fontSize: '0.65rem',
                                fontWeight: 800,
                                textTransform: 'uppercase'
                            }}>
                                {row.status || 'Active'}
                            </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                            <ReferralProgressCell
                                current={row.transaction_count.count}
                                total={row.transaction_count.minCount}
                                percent={row.transaction_count.percent}
                                theme={theme}
                            />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                            <ReferralProgressCell
                                current={row.transaction_amount.amount}
                                total={row.transaction_amount.minAmount}
                                percent={row.transaction_amount.percent}
                                isCurrency
                                theme={theme}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
));

const MobileCardView = React.memo(({ data, theme }) => (
    <Grid container spacing={1}>
        {data.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
                <ReferralCard item={item} theme={theme} />
            </Grid>
        ))}
    </Grid>
));

const Referral = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    
    const clientId = useSelector(state => state.profile.userData?.client_id);
    const brokerSettings = useSelector(state => state.profile.brokerSettings);
    
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ totalReferrals: 0, totalBonus: 0, pendingBonus: 0 });

    useEffect(() => {
        if (brokerSettings && brokerSettings.referral_dashboard !== "true") {
            navigate('/');
        }
    }, [brokerSettings, navigate]);

    const fetchData = React.useCallback(async (signal) => {
        if (!clientId) return;
        setLoading(true);
        try {
            let response = { data: [] };
            try {
                const apiRes = await fetchReferralBonus(clientId);
                if (apiRes) response = apiRes;
            } catch (e) {
                console.error("API call failed:", e);
                // Optionally show an error message via snackbar here if desired, 
                // but the catch block below handles general errors.
            }

            if (signal?.aborted) return;
            if (!response || !response.data) {
                setData([]);
                return;
            }

            const rawData = response?.data?.data || [];

            let earnedB = 0;
            let pendingB = 0;
            let totalR = 0;

            const transformedData = rawData.map(item => {
                if (!item || !item.client_id) return null;

                const bonus = parseFloat(item.referal_bonus) || 0;
                const status = (item.bonus_transaction_status || 'pending').toLowerCase();

                totalR += 1;
                if (status === 'completed') {
                    earnedB += bonus;
                } else {
                    pendingB += bonus;
                }

                const count = parseInt(item.transaction_count) || 0;
                const minCount = parseInt(item.min_transaction_count) || 1;
                const amount = parseFloat(item.transaction_amount) || 0;
                const minAmount = parseFloat(item.min_transaction_amount) || 1;

                const countPercent = Math.max(0, Math.min((count / minCount) * 100, 100));
                const amountPercent = Math.max(0, Math.min((amount / minAmount) * 100, 100));

                return {
                    referred_client: item.client_id,
                    narration: item.narration || '-',
                    date: item.signup_date ? item.signup_date.split(' ')[0] : 'N/A',
                    bonus: item.referal_bonus || '0',
                    status: item.bonus_transaction_status || 'Pending',
                    transaction_count: { count, minCount, percent: countPercent },
                    transaction_amount: { amount, minAmount, percent: amountPercent }
                };
            }).filter(Boolean);

            setData(transformedData);
            setSummary({
                totalReferrals: totalR,
                totalBonus: earnedB,
                pendingBonus: pendingB
            });
        } catch (error) {
            if (signal?.aborted) return;
            console.error("Error fetching referral bonus:", error);
            enqueueSnackbar('Failed to fetch referral data', { variant: 'error' });
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [clientId, enqueueSnackbar]);

    useEffect(() => {
        const controller = new AbortController();
        fetchData(controller.signal);
        return () => controller.abort();
    }, [fetchData]);

    const handleManualRefresh = () => {
        fetchData();
    };

    return (
        <Box sx={{
            height: '100%',
            width: '100%',
            p: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 1.5 : 3, // Slightly reduced gap for mobile
            overflowY: 'auto', // ROOT SCROLL: THE PAGE SCROLLER
            overflowX: 'hidden',
            position: 'relative',
        }}>
            {/* Header section - Outside table */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                flex: 'none'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                        }}
                    >
                        <ArrowBackIcon fontSize="small" color="primary" />
                    </IconButton>
                    <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ color: 'text.primary' }}>
                        Referral Dashboard
                    </Typography>
                </Box>
                <Box>
                    <Tooltip title="Refresh data">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleManualRefresh}
                            disabled={loading}
                            sx={{ borderRadius: 2 }}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

        {/* Metrics Loading State */}
        {loading ? (
            <MetricsSkeleton theme={theme} />
        ) : (
            isMobile ? (
                <Card sx={{
                    borderRadius: 4,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    mb: 3,
                    width: '100%',
                    backgroundColor: theme.palette.background.paper,
                    flex: 'none'
                }}>
                    <CardContent sx={{ p: '24px !important' }}>
                        <Grid container spacing={0}>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5, fontSize: '0.65rem' }}>REFERRALS</Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, color: theme.palette.text.primary }}>{summary.totalReferrals}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5, fontSize: '0.65rem' }}>EARNED</Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.success.main, lineHeight: 1.2 }}>₹{summary.totalBonus}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5, fontSize: '0.65rem' }}>PENDING</Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.warning.main, lineHeight: 1.2 }}>₹{summary.pendingBonus}</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ width: '100%', mb: 1, flex: 'none' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <SummaryCard
                                title="Total Active Referrals"
                                value={summary.totalReferrals}
                                icon={<GroupIcon />}
                                color={theme.palette.primary.main}
                                theme={theme}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <SummaryCard
                                title="Earned Bonus"
                                value={summary.totalBonus}
                                icon={<StarsIcon />}
                                color={theme.palette.success.main}
                                theme={theme}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <SummaryCard
                                title="Pending Bonus"
                                value={summary.pendingBonus}
                                icon={<AccountBalanceWalletIcon />}
                                color={theme.palette.warning.main}
                                theme={theme}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )
        )}

            {isMobile ? (
                <Box sx={{ width: '100%', mb: 4 }}>
                    <Box sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.2rem', color: theme.palette.text.primary }}>
                            History
                        </Typography>
                    </Box>
                    {loading ? (
                        <ReferralSkeleton isMobile={isMobile} theme={theme} />
                    ) : data.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
                            <Typography variant="body2">No referral data found</Typography>
                        </Box>
                    ) : (
                        <MobileCardView data={data} theme={theme} />
                    )}
                </Box>
            ) : (
                <Paper sx={{
                    flex: 'none',
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    // minHeight removed to allow shrink-to-fit
                }}>
                    <Box sx={{
                        p: 2.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: alpha(theme.palette.primary.main, 0.03),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flex: 'none'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.2rem', color: theme.palette.text.primary }}>
                                History
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        flex: 1,
                        overflowX: 'hidden',
                        p: 1.5,
                        backgroundColor: alpha(theme.palette.background.default, 0.4),
                        borderRadius: 'inherit'
                    }}>
                        {loading ? (
                            <ReferralSkeleton isMobile={isMobile} theme={theme} />
                        ) : data.length === 0 ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                p: 10,
                                textAlign: 'center'
                            }}>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: '50%',
                                    backgroundColor: alpha(theme.palette.text.disabled, 0.1),
                                    color: theme.palette.text.disabled,
                                    mb: 1.5
                                }}>
                                    <AssessmentIcon sx={{ fontSize: 48 }} />
                                </Box>
                                <Typography variant="h6" color="text.secondary" fontWeight={600}>
                                    No referral data found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Once you start earning referral bonuses, they will appear here.
                                </Typography>
                            </Box>
                        ) : (
                            <DesktopTableView data={data} theme={theme} />
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default Referral;
